import * as moment from 'moment';
import * as RecordRTC from 'recordrtc';
import { BehaviorSubject, Subject, Subscription, combineLatest } from 'rxjs';
import fixWebmDuration from 'fix-webm-duration';
import { IconMessageToasterElement } from 'src/app/ui/notification-toaster/icon-message-toaster-element/icon-message-toaster-element.component';
import {
  ButtonToasterElement,
  ButtonToasterElementStyle,
} from 'src/app/ui/notification-toaster/button-toaster-element/button-toaster-element.component';
import { ERRORS, WARNINGS } from 'src/app/common/utils/notification-constants';
import {
  NotificationDataBuilder,
  NotificationToasterService,
  NotificationType,
} from 'src/app/services/notification-toaster.service';
import { ToasterPopupStyle } from 'src/app/ui/notification-toaster/custom-notification-toastr/custom-notification-toastr.component';
import { TranslateService } from '@ngx-translate/core';
import { DevicesManagerService } from 'src/app/services/devices-manager.service';
import {
  trackSubject,
  VideoCallTracksStateService,
} from 'src/app/services/video-call-tracks-state.service';
import { isNil } from 'lodash';
import { TrackType } from 'src/app/common/interfaces/rtc-interface';
import { DevicesHandleUtil } from 'src/app/common/utils/devices-handle-util';
import { modifiedSetTimeout } from 'src/app/utilities/ZoneUtils';
import { ProviderStateService } from 'src/app/services/provider-state.service';
import { CustomerSupportService } from 'src/app/services/customer-support.service';
import { UserService } from 'src/app/services/user.service';
import { intercomArticles } from '../../../intercom-articles';
import { RECORD_STATUS } from '../../../models/recording';
import {
  currentSupportedMimeType,
  storeCompleteEntry,
  createRecordingEntry,
  mergeBlobs,
  RecordingMetadata,
} from './recording-db';

declare global {
  interface MediaDevices {
    getDisplayMedia(constraints?: MediaStreamConstraints): Promise<MediaStream>;
  }
}

type supportedMimeType = 'video/webm' | 'video/mp4';

const recordOptions: RecordRTC.Options = {
  type: 'video',
  mimeType: currentSupportedMimeType() as supportedMimeType, // or video/webm\;codecs=h264 or video/webm\;codecs=vp9
  audioBitsPerSecond: 128000,
  videoBitsPerSecond: 1280000,
  bitsPerSecond: 1024000, // if this line is provided, skip above two
  // Doing this explicitly right now, since while the recordrtc library has been patched to increase
  // default recording time to 24hrs from 1hr, it hasn't been released yet.
  timeSlice: 24 * 60 * 60 * 1000,
};

const mediaConstraints: MediaStreamConstraints = {
  video: {
    width: { max: 1920 },
    height: { max: 1080 },
    frameRate: { ideal: 10 },
  },
};

/**
 * Time slice for recording chunks in milliseconds (60 seconds).
 * This value is critical for duration calculation and should not be changed
 * without updating the fixWebmDuration function for previous recordings.
 */
const RECORDING_TIME_SLICE_MS = 1000 * 60; // 60 seconds

// Class that automatically calls callbacks when a set of keys has changed
class KeyManager<T> {
  constructor(private createFunc: (key: string) => T, private destroyFunc: (key: string) => void) {}

  private keySet: Set<string> = new Set();

  public updateKeys(keys: string[]): T[] {
    const newKeys = new Set(keys);

    /*
     * oldKeys: [a,b,c]
     * newKeys: [a,b]
     * oldKeys - newKeys = [c]
     */
    const removedKeys = this.setDifference(this.keySet, newKeys);

    /*
     * oldKeys: [a,b]
     * newKeys: [a,b,c]
     * newKeys - oldKeys = [c]
     */
    const addedKeys = this.setDifference(newKeys, this.keySet);

    removedKeys.forEach((key) => {
      // Call the destroyFunc when a key is removed from the set
      this.destroyFunc(key);
    });

    const ret: T[] = [];
    // Call the createFunc when a key is added to the set,
    // store the result so it can be returned
    addedKeys.forEach((key) => {
      ret.push(this.createFunc(key));
    });

    // Update the set of keys
    this.keySet = new Set(keys);
    return ret;
  }

  // adapted from: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Set
  private setDifference(a: Set<string>, b: Set<string>) {
    const _difference = new Set(a);
    for (const elem of b) {
      _difference.delete(elem);
    }
    return _difference;
  }
}

// Handles getting the trackStates for each participant
// This is non trivial because the states are held in observables
// this means that we need to keep track of which participants are in the call
// and subscribe / unsubscribe when they are added / removed
class TrackHandler {
  private keyManager = new KeyManager(
    this.subscribeUserTrack.bind(this),
    this.unsubscribeUserTrack.bind(this),
  );

  private keySubscription?: Subscription;
  private trackSubscriptions: { [key: string]: Subscription | undefined } = {};
  private trackStates: { [key: string]: trackSubject } = {};
  public trackStatesObservable: BehaviorSubject<{ [key: string]: trackSubject }> =
    new BehaviorSubject<{ [key: string]: trackSubject }>({});

  private localParticipantId?: string;
  private localParticipantIdSubscription?: Subscription;

  constructor(
    private videoCallTracksStateService: VideoCallTracksStateService,
    private devicesManagerService: DevicesManagerService,
    private providerStateService: ProviderStateService,
  ) {}

  public subscribeToTracks(): void {
    // Subscribe to local participant ID changes
    this.localParticipantIdSubscription = this.providerStateService.localParticipantId$.subscribe(
      (localParticipantId) => {
        this.localParticipantId = localParticipantId;
      },
    );

    const managerKeys = this.videoCallTracksStateService.participantTracksStateMap.managerKeys;

    this.keySubscription = managerKeys.subscribe((participantIds) => {
      if (!participantIds) {
        return;
      }
      this.keyManager.updateKeys(participantIds);
    });
  }

  public unsubscribeFromTracks(): void {
    Object.values(this.trackSubscriptions).forEach((x) => x?.unsubscribe());
    this.keySubscription?.unsubscribe();
    this.localParticipantIdSubscription?.unsubscribe();
  }

  private subscribeUserTrack(participantId: string) {
    const isLocalParticipant = participantId === this.localParticipantId;

    if (isLocalParticipant) {
      // For local participant, combine track and playing state observables
      const trackObservable = this.videoCallTracksStateService.getPariticpantTrackObservable(
        participantId,
        TrackType.VIDEO,
        this.devicesManagerService,
        true, // isAudio = true
      );

      const playingObservable =
        this.videoCallTracksStateService.getPariticpantTrackPlayingObservable(
          participantId,
          TrackType.VIDEO,
          this.devicesManagerService,
          true, // isAudio = true
        );

      // Combine both observables to handle local participant muting
      this.trackSubscriptions[participantId] = combineLatest([
        trackObservable,
        playingObservable,
      ]).subscribe(([audioTrack, isPlaying]) => {
        // For local participant: if muted (not playing), remove track from recording
        // If playing, include the track
        if (audioTrack && isPlaying) {
          this.trackStates[participantId] = audioTrack.clone();
        } else {
          // Either no track or muted - remove from recording
          this.trackStates[participantId] = null;
        }

        this.trackStatesObservable.next(this.trackStates);
      });
    } else {
      this.trackSubscriptions[participantId] = this.videoCallTracksStateService
        .getPariticpantTrackObservable(
          participantId,
          TrackType.VIDEO,
          this.devicesManagerService,
          true,
        )
        .subscribe((audioTrack) => {
          this.trackStates[participantId] = audioTrack;
          this.trackStatesObservable.next(this.trackStates);
        });
    }
  }

  private unsubscribeUserTrack(participantId: string) {
    this.trackSubscriptions[participantId]?.unsubscribe();
    this.trackStates[participantId] = null;
    this.trackStatesObservable.next(this.trackStates);
  }
}

export class Recorder {
  private trackHandler: TrackHandler;
  private recordingId: string | null = null;

  // Holds the status of the recorder
  // READY: the recorder is ready to record
  // ACTIVE: the recorder is currently recording
  private _recordingStatus = new BehaviorSubject(RECORD_STATUS.READY);

  public recording$ = this._recordingStatus.asObservable();

  public set recording(recordingStatus: RECORD_STATUS) {
    this._recordingStatus.next(recordingStatus);
  }
  public get recording(): RECORD_STATUS {
    return this._recordingStatus.getValue();
  }

  public RECORD_STATUS: typeof RECORD_STATUS = RECORD_STATUS;

  // This is the underlying recorder that records a MediaStream that is provided
  private recordRTC?: RecordRTC;

  // The screen capture mediaStream
  private captureStream?: MediaStream;

  // The stream that contains the desktop audio/video as well as the daily.co tracks
  private combinedStream: MediaStream = new MediaStream();

  private desktopAudioStreamSubject: BehaviorSubject<MediaStreamTrack | null> =
    new BehaviorSubject<MediaStreamTrack | null>(null);

  private trackStateSubscription?: Subscription;
  private desktopAudioStreamSubscription?: Subscription;

  currentAddedAudioTracksIDs: string[] = [];
  // Store temp media streams so we can manipulate their enabled state
  private tempMediaStreams: Map<string, MediaStream> = new Map();

  // Track recording start time for duration calculation
  private recordingStartTime?: number;

  // Store merged chunk in memory
  private mergedChunk: Blob | null = null;

  // Store complete metadata in memory to avoid database reads
  private recordingMetadata: RecordingMetadata | null = null;

  // Whether IndexedDB is available for this recording session.
  // When false the recording continues in memory only and the user can still download at the end.
  private isIndexedDbAvailable = true;

  public output$: Subject<{ file: File; recordingId: string }> = new Subject();

  constructor(
    private title: string,
    private translateService: TranslateService,
    private notificationToasterService: NotificationToasterService,
    private devicesManagerService: DevicesManagerService,
    public videoCallTracksStateService: VideoCallTracksStateService,
    protected providerStateService: ProviderStateService,
    private customerSupportService: CustomerSupportService,
    private userService: UserService,
    private preferCurrentTab: boolean = false,
  ) {
    this.trackHandler = new TrackHandler(
      videoCallTracksStateService,
      devicesManagerService,
      providerStateService,
    );
  }

  private async addVideoStreams(): Promise<void> {
    try {
      const displayMediaOptions: any = {
        ...mediaConstraints,
        preferCurrentTab: this.preferCurrentTab,
      };
      const stream = await navigator.mediaDevices.getDisplayMedia(
        displayMediaOptions as MediaStreamConstraints,
      );
      // Note: if the user is also sharing their audio then we technically duplicate the
      //		 audio that is being recorded. From my testing this seems to work okay and
      //       they are in sync, but this is not definitively true
      stream.getTracks().forEach((track) => {
        if (track.kind === 'video') {
          this.combinedStream.addTrack(track);
        } else {
          this.desktopAudioStreamSubject.next(track);
        }
      });
      this.captureStream = stream;
      this.addStreamStopListener(stream);
    } catch (err) {
      this.captureStartScreenRecordingError(err.toString());
      throw new Error(err);
    }
  }

  private captureStartScreenRecordingError(error: string) {
    // 1st check: To capture the permission is disabled at OS level on Firefox only, and
    // to capture the permission is disabled at OS level on Chrome, Edge and at browser level on Safari
    // 2nd check: To capture all un-discovered errors, and keeping not showing any error notification once canceling the share screen popup on Chrome, and Edge
    if (
      this.devicesManagerService.isNotFoundError(error) ||
      (this.devicesManagerService.isNotAllowedError(error) &&
        (DevicesHandleUtil.isDeniedBySystem(error) ||
          this.devicesManagerService.isNotAllowedByUserAgent(error)))
    ) {
      this.showGrantScreenRecordingAccessNotification();
      this.devicesManagerService.logStartScreenErrorsToFullStory(
        'Screen recording: Permission Denied',
      );
    } else if (
      !(
        this.devicesManagerService.isNotAllowedError(error) &&
        !(
          DevicesHandleUtil.isDeniedBySystem(error) &&
          this.devicesManagerService.isNotAllowedByUserAgent(error)
        )
      )
    ) {
      this.showScreenRecordingFailedNotification();
      this.devicesManagerService.logStartScreenErrorsToFullStory(
        'Screen recording: Failed to start',
      );
    }
  }

  private showGrantScreenRecordingAccessNotification() {
    const title = new IconMessageToasterElement(
      { icon: 'screen_share', size: 16 },
      this.translateService.instant('Screen recording access denied'),
    );
    const message = new IconMessageToasterElement(
      undefined,
      this.translateService.instant(
        "Please enable screen recording permissions for your browser in your computer's System Preferences.",
      ),
    );
    const screenShareHelpButton = new ButtonToasterElement(
      [{ icon: 'help_outline', size: 16 }, this.translateService.instant('Need help?')],
      {
        handler: () => {
          this.customerSupportService.openLink(
            intercomArticles.HowToEnableScreenSharingPermissions,
          );
        },
        close: false,
      },
      ButtonToasterElementStyle.RAISED,
    );
    const grantShareScreenNotificationData = new NotificationDataBuilder(
      ERRORS.GRANT_SCREENSHARE_ACCESS,
    )
      .type(NotificationType.ERROR)
      .style(ToasterPopupStyle.ERROR)
      .topElements([title])
      .middleElements([message])
      .bottomElements([screenShareHelpButton])
      .priority(980)
      .dismissable(true)
      .build();
    this.notificationToasterService.showNotification(grantShareScreenNotificationData);
  }

  private showScreenRecordingFailedNotification() {
    const title = new IconMessageToasterElement(
      { icon: 'screen_share', size: 16 },
      this.translateService.instant('Screen recording failed'),
    );
    const message = new IconMessageToasterElement(
      undefined,
      this.translateService.instant(
        'Something went wrong while trying to start the screen recording. Please try again. If you continue to run into issues, please restart your browser.',
      ),
    );
    const screenShareHelpButton = new ButtonToasterElement(
      [{ icon: 'help_outline', size: 16 }, this.translateService.instant('Need help?')],
      {
        handler: () => {
          this.customerSupportService.openLink(intercomArticles.WhatToDoWhenYourScreenShareFails);
        },
        close: false,
      },
      ButtonToasterElementStyle.RAISED,
    );
    const grantShareScreenNotificationData = new NotificationDataBuilder(
      ERRORS.GRANT_SCREENSHARE_ACCESS,
    )
      .type(NotificationType.ERROR)
      .style(ToasterPopupStyle.ERROR)
      .topElements([title])
      .middleElements([message])
      .bottomElements([screenShareHelpButton])
      .priority(980)
      .dismissable(true)
      .build();
    this.notificationToasterService.showNotification(grantShareScreenNotificationData);
  }

  // If the stream from the capture ends this implies the user clicked
  // on the stop sharing popup. In this case stop recording
  private addStreamStopListener(stream: MediaStream): void {
    stream.addEventListener('ended', () => this.stopRecording());
    stream.addEventListener('inactive', () => this.stopRecording());
    stream.getTracks().forEach((track) => {
      track.addEventListener('ended', () => this.stopRecording());
      track.addEventListener('inactive', () => this.stopRecording());
    });
  }

  // For some reason the recorder does not record until there is a track in the combined
  // media stream destination. This is a dummy track to trick the recorder into starting
  private dummyAudioTrack() {
    const ctx = new AudioContext();
    const oscillator = ctx.createOscillator();
    const dst = ctx.createMediaStreamDestination();
    oscillator.connect(dst);
    return dst.stream.getTracks()[0];
  }

  // Adds the audio streams to the combined stream
  // Uses a different audio context to avoid edge cases
  // where the recorder fails when dynamically adding tracks
  // see: https://github.com/muaz-khan/RecordRTC/issues/509
  public addAudioStreams(): void {
    const audioContext = new AudioContext();
    const destination = audioContext.createMediaStreamDestination();

    this.desktopAudioStreamSubscription?.unsubscribe();
    this.desktopAudioStreamSubscription = this.desktopAudioStreamSubject.subscribe(
      (mediaStreamTrack) => {
        if (!mediaStreamTrack) {
          return;
        }
        const tempMediaStream = new MediaStream();
        tempMediaStream.addTrack(mediaStreamTrack);
        audioContext.createMediaStreamSource(tempMediaStream).connect(destination);
      },
    );

    this.trackStateSubscription?.unsubscribe();
    this.trackStateSubscription = this.trackHandler.trackStatesObservable.subscribe(
      (trackStates) => {
        const audioTracks = Object.values(trackStates).filter((x) => !isNil(x));
        if (audioTracks) {
          this.handleRemoteAudioTracks(audioTracks, audioContext, destination);
        }
      },
    );

    // The recorder will not run until a track is in the destination node
    // So we add a dummy track with no sound to get the recorder to start
    const temp = new MediaStream();
    temp.addTrack(this.dummyAudioTrack());
    audioContext.createMediaStreamSource(temp).connect(destination);

    // This will produce a single track that contains the audio for all other
    // tracks. This avoids the issue of dynamically adding tracks while the
    // recorder is running
    const tracks = destination.stream.getTracks();
    tracks.forEach((track) => this.combinedStream.addTrack(track));
  }

  private handleRemoteAudioTracks(
    audioTracks: MediaStreamTrack[] | trackSubject[],
    audioContext: AudioContext,
    destination: MediaStreamAudioDestinationNode,
  ) {
    // Get current track IDs that should be active
    const currentTrackIds = audioTracks.filter((track) => track?.id).map((track) => track!.id);

    // 1. Handle tracks that should be disabled (e.g., muted local participant)
    this.currentAddedAudioTracksIDs.forEach((trackId) => {
      const tempStream = this.tempMediaStreams.get(trackId);
      if (tempStream) {
        const shouldBeEnabled = currentTrackIds.includes(trackId);
        tempStream.getAudioTracks().forEach((track) => {
          track.enabled = shouldBeEnabled;
        });
      }
    });

    // 2. Add truly new tracks that aren't already being tracked
    audioTracks.forEach((audioTrack) => {
      if (!audioTrack?.id) {
        return;
      }

      // Only add if not already added
      if (!this.currentAddedAudioTracksIDs.includes(audioTrack.id)) {
        // Add to our tracking
        this.currentAddedAudioTracksIDs.push(audioTrack.id);

        // Create temp media stream and store it
        const tempMediaStream = new MediaStream();
        tempMediaStream.addTrack(audioTrack);
        this.tempMediaStreams.set(audioTrack.id, tempMediaStream);

        // Connect to audio context
        audioContext.createMediaStreamSource(tempMediaStream).connect(destination);

        // Clean up when track ends
        audioTrack.addEventListener(
          'ended',
          () => {
            // Remove from tracking and temp streams
            this.currentAddedAudioTracksIDs = this.currentAddedAudioTracksIDs.filter(
              (id) => id !== audioTrack.id,
            );
            this.tempMediaStreams.delete(audioTrack.id);
          },
          { once: true },
        );
      }
    });
  }

  private async setupCombinedStream() {
    this.combinedStream = new MediaStream();
    await this.addVideoStreams();
    this.addAudioStreams();
  }

  public async startRecording(): Promise<void> {
    try {
      this.trackHandler.subscribeToTracks();
      await this.setupCombinedStream();
      this.recordingStartTime = Date.now();
      // Reset merged chunk and metadata for new recording
      this.mergedChunk = null;
      this.recordingMetadata = null;
      this.isIndexedDbAvailable = true;
      const userId = this.userService.userId;
      if (!userId) {
        throw new Error('User ID not available for recording');
      }
      this.recordingId = `${userId}-${this.recordingStartTime}`;

      // Initialize metadata in memory
      this.recordingMetadata = {
        title: this.title,
        timestamp: this.recordingStartTime,
        duration: 0, // Will be updated with each chunk
      };

      try {
        await createRecordingEntry(this.recordingId, this.title, this.recordingStartTime);
      } catch (dbError) {
        console.warn('IndexedDB unavailable, recording will continue in memory only:', dbError);
        this.isIndexedDbAvailable = false;
        this.notificationToasterService.showDefaultWarningNotificationV2(
          this.translateService.instant('Download Required'),
          this.translateService.instant(
            'Recording won’t be saved after closing or refreshing this tab. Download it before ending your session.',
          ),
          WARNINGS.RECORDING_INDEXEDDB_UNAVAILABLE,
        );
      }

      const onDataAvailable = (blob: Blob) => {
        if (!this.recordingId || !this.recordingMetadata || !this.recordingStartTime) {
          console.error('Recording state not properly initialized');
          return;
        }

        // Merge the new chunk with the existing merged chunk
        this.mergedChunk = mergeBlobs(this.mergedChunk, blob);

        // Update duration in memory
        const currentDuration = Date.now() - this.recordingStartTime;
        this.recordingMetadata.duration = currentDuration;

        // Only persist to IndexedDB when it is available
        if (this.isIndexedDbAvailable) {
          storeCompleteEntry(this.recordingId, this.mergedChunk, this.recordingMetadata);
        }
      };

      const customRecordOptions = {
        ...recordOptions,
        timeSlice: RECORDING_TIME_SLICE_MS,
        ondataavailable: onDataAvailable,
      };
      const recordRTC = new RecordRTC(this.combinedStream, customRecordOptions);
      this.recordRTC = recordRTC;
      this.recording = RECORD_STATUS.ACTIVE;
      // Implement Soln from : https://github.com/muaz-khan/RecordRTC/issues/738
      await new Promise<void>((resolve) => {
        modifiedSetTimeout(() => {
          recordRTC.startRecording();
          resolve();
        }, 2000);
      });
    } catch (err) {
      console.log(err);
      this.stopRecording();
    }
  }

  public pauseRecording(): void {
    try {
      this.recordRTC?.pauseRecording();
      this.recording = RECORD_STATUS.PAUSED;
    } catch (err) {
      console.log(err);
      this.stopRecording();
    }
  }

  public resumeRecording(): void {
    try {
      this.recordRTC?.resumeRecording();
      this.recording = RECORD_STATUS.ACTIVE;
    } catch (err) {
      console.log(err);
      this.stopRecording();
    }
  }

  public async stopRecording(): Promise<void> {
    this.trackHandler.unsubscribeFromTracks();
    this.trackStateSubscription?.unsubscribe();

    // Clean up tracking and temp streams
    this.currentAddedAudioTracksIDs = [];
    this.tempMediaStreams.clear();

    const recordingId = this.recordingId;

    const blobPromise: Promise<Blob | undefined> = new Promise((resolve) => {
      this.recordRTC?.stopRecording(async () => {
        if (!recordingId) {
          return resolve(undefined);
        }
        // Use the merged chunk from memory instead of getting from RecordRTC
        resolve(this.mergedChunk || undefined);
      });
    });

    let blob = await blobPromise;
    if (!blob) {
      // If there's no blob, we can't proceed, but we shouldn't throw an error, just return.
      // This can happen if recording is stopped very quickly.
      return;
    }

    // Fix WEBM duration metadata if the format is WEBM
    let duration: number | undefined;
    if (currentSupportedMimeType() === 'video/webm' && this.recordingStartTime) {
      duration = Date.now() - this.recordingStartTime;
      // Duration is already stored in IndexedDB via storeCompleteEntry() in onDataAvailable
      // This duration calculation is only needed for fixWebmDuration() below
      try {
        blob = await fixWebmDuration(blob, duration, { logger: false });
      } catch (error) {
        console.warn('Failed to fix WEBM duration:', error);
        // Continue with original blob if fixing fails
      }
    }

    const date = moment(new Date());
    const file = new File([blob!], `${this.title}-${date.format('DD-MM-YYYY')}`, {
      type: currentSupportedMimeType(),
    });
    this.combinedStream = new MediaStream();
    this.captureStream?.getTracks().forEach((track) => track.stop());

    // Reset recording start time and merged chunk
    this.recordingStartTime = undefined;
    this.mergedChunk = null;
    this.recordingMetadata = null;

    // This is here to make stopRecording idempotent, so if its called
    // multiple times the state remains the same
    if (this.recording !== RECORD_STATUS.READY) {
      this.recording = RECORD_STATUS.FINISHED;
      this.recording = RECORD_STATUS.READY;
      // Emit file with recordingId before clearing it
      // recordingId should always be set at this point since we check it earlier
      if (!recordingId) {
        throw new Error('Recording ID is required but was not set');
      }
      this.output$.next({ file, recordingId });
    }
    this.recordingId = null;
  }
}
